const SVG_NS = "http://www.w3.org/2000/svg";

export type IconName =
  | "edit"
  | "copy"
  | "paste"
  | "lock"
  | "unlock"
  | "trash"
  | "more"
  | "layer-up"
  | "layer-down"
  | "layer-to-top"
  | "layer-to-bottom"
  | "node-remove"
  | "split"
  | "line-extend"
  | "debug"
  | "close"
  | "palette"
  | "roller"
  | "route"
  | "plus"
  | "minus"
  | "layers"
  | "chevron-down"
  | "check"
  | "play"
  | "pause";

interface IconElementSpec {
  tag: "path" | "rect" | "circle";
  attrs: Record<string, string>;
}

/**
 * Icônes "backoffice" monochromes (contour `currentColor`, pas d'emoji) — voir `createIcon`. Chaque
 * icône hérite couleur/épaisseur de trait du `<svg>` parent ; un élément peut les redéfinir
 * localement (ex. les points de `"more"`, remplis plutôt que tracés).
 */
const ICONS: Record<IconName, IconElementSpec[]> = {
  edit: [
    { tag: "path", attrs: { d: "M3 21v-3.75L14.81 5.44a1.5 1.5 0 0 1 2.12 0l1.63 1.63a1.5 1.5 0 0 1 0 2.12L6.75 21H3Z" } },
    { tag: "path", attrs: { d: "M13.5 6.75l3.75 3.75" } },
  ],
  copy: [
    { tag: "rect", attrs: { x: "9", y: "9", width: "12", height: "12", rx: "2" } },
    { tag: "path", attrs: { d: "M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" } },
  ],
  paste: [
    { tag: "path", attrs: { d: "M9 3h6a1 1 0 0 1 1 1v2H8V4a1 1 0 0 1 1-1Z" } },
    { tag: "rect", attrs: { x: "5", y: "5", width: "14", height: "16", rx: "2" } },
    { tag: "path", attrs: { d: "M9 12h6" } },
    { tag: "path", attrs: { d: "M9 16h6" } },
  ],
  lock: [
    { tag: "rect", attrs: { x: "5", y: "11", width: "14", height: "9", rx: "2" } },
    { tag: "path", attrs: { d: "M8 11V8a4 4 0 0 1 8 0v3" } },
    { tag: "circle", attrs: { cx: "12", cy: "15.5", r: "1.3", fill: "currentColor", stroke: "none" } },
  ],
  unlock: [
    { tag: "rect", attrs: { x: "5", y: "11", width: "14", height: "9", rx: "2" } },
    { tag: "path", attrs: { d: "M8 11V8a4 4 0 0 1 7.5-2" } },
    { tag: "circle", attrs: { cx: "12", cy: "15.5", r: "1.3", fill: "currentColor", stroke: "none" } },
  ],
  trash: [
    { tag: "path", attrs: { d: "M4 7h16" } },
    { tag: "path", attrs: { d: "M6 7l1 12.5A2 2 0 0 0 9 21h6a2 2 0 0 0 2-2.5L18 7" } },
    { tag: "path", attrs: { d: "M9.5 7V4.5A1.5 1.5 0 0 1 11 3h2a1.5 1.5 0 0 1 1.5 1.5V7" } },
    { tag: "path", attrs: { d: "M10 11v6" } },
    { tag: "path", attrs: { d: "M14 11v6" } },
  ],
  more: [
    { tag: "circle", attrs: { cx: "5", cy: "12", r: "1.5", fill: "currentColor", stroke: "none" } },
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "1.5", fill: "currentColor", stroke: "none" } },
    { tag: "circle", attrs: { cx: "19", cy: "12", r: "1.5", fill: "currentColor", stroke: "none" } },
  ],
  "layer-up": [
    { tag: "path", attrs: { d: "M12 20V6" } },
    { tag: "path", attrs: { d: "M6 11l6-6 6 6" } },
  ],
  "layer-down": [
    { tag: "path", attrs: { d: "M12 4v14" } },
    { tag: "path", attrs: { d: "M6 13l6 6 6-6" } },
  ],
  // "Monter tout en haut" : même flèche que "layer-up", plus une barre qui marque la butée du
  // calque maximal (distingue visuellement "un pas" de "jusqu'au bout").
  "layer-to-top": [
    { tag: "path", attrs: { d: "M5 4h14" } },
    { tag: "path", attrs: { d: "M12 20V9" } },
    { tag: "path", attrs: { d: "M7 13l5-5 5 5" } },
  ],
  // "Descendre tout en bas" : symétrique de "layer-to-top".
  "layer-to-bottom": [
    { tag: "path", attrs: { d: "M5 20h14" } },
    { tag: "path", attrs: { d: "M12 4v11" } },
    { tag: "path", attrs: { d: "M7 11l5 5 5-5" } },
  ],
  // Menu contextuel d'un sommet en édition ("Retirer le point", voir `createTokaMap.ts`) : le
  // sommet (cercle) barré d'un simple trait — un "moins" générique, cohérent avec "trash" (forme
  // entière) sans s'y confondre.
  "node-remove": [
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "7" } },
    { tag: "path", attrs: { d: "M9 12h6" } },
  ],
  // "Diviser ici" : 2 segments séparés par le sommet coupé (petit point plein au milieu) — la ligne/
  // le contour du polygone se rompt à cet endroit précis.
  split: [
    { tag: "path", attrs: { d: "M4 12h5" } },
    { tag: "path", attrs: { d: "M15 12h5" } },
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "1.6", fill: "currentColor", stroke: "none" } },
  ],
  // "Continuer la ligne" : un trait qui se prolonge, terminé par un chevron — même idée que
  // "layer-up"/"layer-down" (une direction), ici horizontale plutôt que verticale.
  "line-extend": [
    { tag: "path", attrs: { d: "M3 12h10" } },
    { tag: "path", attrs: { d: "M14 8l4 4-4 4" } },
  ],
  // Bouton "diagnostic" de `FpsMeter` : une silhouette d'insecte (tête/corps/antennes/pattes) —
  // délibérément PAS une molette de réglages, trop proche visuellement du bouton clair/sombre déjà
  // présent dans `Header` (confondu avec lui en pratique, voir historique de conception) — un
  // insecte reste sans ambiguïté associé au "debug" quelle que soit l'UI environnante.
  debug: [
    { tag: "circle", attrs: { cx: "12", cy: "6.5", r: "2" } },
    { tag: "rect", attrs: { x: "8.5", y: "8.5", width: "7", height: "10", rx: "3.5" } },
    { tag: "path", attrs: { d: "M10 4.5L8.5 2.5" } },
    { tag: "path", attrs: { d: "M14 4.5l1.5-2" } },
    { tag: "path", attrs: { d: "M8.5 11H4" } },
    { tag: "path", attrs: { d: "M8.5 15H4.5" } },
    { tag: "path", attrs: { d: "M15.5 11H20" } },
    { tag: "path", attrs: { d: "M15.5 15h4" } },
  ],
  // Fermeture (bouton "×" de `FpsMeter`'s modale) : une croix vectorielle plutôt qu'un caractère
  // `"×"` texte, dont le rendu (taille/alignement) varie trop selon la police du navigateur.
  close: [
    { tag: "path", attrs: { d: "M6 6l12 12" } },
    { tag: "path", attrs: { d: "M18 6L6 18" } },
  ],
  // "Copier le style" : une palette de peintre (silhouette + pouce) avec 3 touches de couleur —
  // distincte de "copy" (qui copie la forme entière), pour que les deux items voisins du menu
  // contextuel restent identifiables sans lire le label.
  palette: [
    {
      tag: "path",
      attrs: {
        d: "M12 3a9 9 0 1 0 0 18c1.1 0 1.8-.6 1.8-1.5 0-.5-.2-.9-.2-1.4 0-.8.7-1.3 1.6-1.3H17a4 4 0 0 0 4-4c0-5.5-4.5-9.8-9-9.8Z",
      },
    },
    { tag: "circle", attrs: { cx: "8", cy: "10.5", r: "1.3", fill: "currentColor", stroke: "none" } },
    { tag: "circle", attrs: { cx: "12", cy: "8", r: "1.3", fill: "currentColor", stroke: "none" } },
    { tag: "circle", attrs: { cx: "16", cy: "10.5", r: "1.3", fill: "currentColor", stroke: "none" } },
  ],
  // "Coller le style" : un rouleau à peinture (tambour + manche) — symétrique de "palette" (source
  // de la couleur vs application de la couleur), même idée que "copy"/"paste".
  roller: [
    { tag: "rect", attrs: { x: "5", y: "4", width: "14", height: "5", rx: "2" } },
    { tag: "path", attrs: { d: "M9 9v3" } },
    { tag: "path", attrs: { d: "M9 12h3a2 2 0 0 1 2 2v2" } },
    { tag: "rect", attrs: { x: "12.5", y: "17", width: "3", height: "4", rx: "1" } },
  ],
  route: [
    { tag: "circle", attrs: { cx: "6", cy: "19", r: "2" } },
    { tag: "path", attrs: { d: "M6 17V11a4 4 0 0 1 4-4h7" } },
    { tag: "path", attrs: { d: "M14 4l3 3-3 3" } },
  ],
  plus: [{ tag: "path", attrs: { d: "M12 5v14M5 12h14" } }],
  minus: [{ tag: "path", attrs: { d: "M5 12h14" } }],
  layers: [
    { tag: "path", attrs: { d: "M12 3l9 5-9 5-9-5 9-5z" } },
    { tag: "path", attrs: { d: "M3 13l9 5 9-5" } },
  ],
  "chevron-down": [{ tag: "path", attrs: { d: "M6 9l6 6 6-6" } }],
  check: [{ tag: "path", attrs: { d: "M5 12l5 5 9-10" } }],
  play: [{ tag: "path", attrs: { d: "M8 5.5v13l10.5-6.5L8 5.5Z", fill: "currentColor" } }],
  pause: [
    { tag: "rect", attrs: { x: "7", y: "5.5", width: "3.5", height: "13", rx: "1", fill: "currentColor", stroke: "none" } },
    { tag: "rect", attrs: { x: "13.5", y: "5.5", width: "3.5", height: "13", rx: "1", fill: "currentColor", stroke: "none" } },
  ],
};

/**
 * Construit une icône SVG monochrome (`currentColor`, hérite donc la couleur du texte du bouton
 * qui la contient) via `createElementNS` — pas d'`innerHTML` — pour rester cohérent avec le reste
 * de `core/ui` (construction DOM directe, voir `ContextMenu.ts`).
 */
export function createIcon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.classList.add("toka-icon");

  for (const spec of ICONS[name]) {
    const el = document.createElementNS(SVG_NS, spec.tag);
    for (const [key, value] of Object.entries(spec.attrs)) {
      el.setAttribute(key, value);
    }
    svg.appendChild(el);
  }

  return svg;
}
